import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { createPollingInterval } from '@/utils/realtimeOptimizer';

interface RequestsByCommission {
  commissionId: string;
  commissionName: string;
  count: number;
}

export const useCommissionRequestsCount = () => {
  const [totalPendingRequests, setTotalPendingRequests] = useState(0);
  const [requestsByCommission, setRequestsByCommission] = useState<RequestsByCommission[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, member } = useAuth();

  const fetchRequestsCount = async () => {
    if (!user || !member) {
      setLoading(false);
      return;
    }

    try {
      // Get commissions where user is admin or moderator
      const { data: userCommissions, error: commissionsError } = await supabase
        .from('commission_members')
        .select('commission_id, member_commissions(name)')
        .eq('member_id', member.id)
        .in('role', ['admin', 'moderator'])
        .eq('status', 'active');

      if (commissionsError) throw commissionsError;

      if (!userCommissions || userCommissions.length === 0) {
        setTotalPendingRequests(0);
        setRequestsByCommission([]);
        setLoading(false);
        return;
      }

      const commissionIds = userCommissions.map(uc => uc.commission_id);

      // Count pending requests for these commissions
      const { data: requests, error: requestsError } = await supabase
        .from('commission_join_requests')
        .select('commission_id, member_commissions(name)')
        .in('commission_id', commissionIds)
        .eq('status', 'pending');

      if (requestsError) throw requestsError;

      // Group by commission
      const grouped = requests?.reduce((acc, req) => {
        const commissionId = req.commission_id;
        const commissionName = (req.member_commissions as any)?.name || 'Commission';
        
        if (!acc[commissionId]) {
          acc[commissionId] = { commissionId, commissionName, count: 0 };
        }
        acc[commissionId].count++;
        return acc;
      }, {} as Record<string, RequestsByCommission>) || {};

      const byCommission = Object.values(grouped);
      const total = byCommission.reduce((sum, c) => sum + c.count, 0);

      setRequestsByCommission(byCommission);
      setTotalPendingRequests(total);
    } catch (error) {
      console.error('Error fetching requests count:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestsCount();

    // OPTIMISATION: Polling au lieu de Realtime (30s)
    const cleanup = createPollingInterval(
      'commission_join_requests', 
      fetchRequestsCount, 
      true
    );

    return cleanup;
  }, [user, member]);

  return {
    totalPendingRequests,
    requestsByCommission,
    loading,
    refetch: fetchRequestsCount
  };
};
